import type {
  LibrarySymbolInfo,
  ResolutionString,
  HistoryCallback,
  PeriodParams,
  SubscribeBarsCallback,
  DatafeedConfiguration,
  ResolveCallback,
  OnReadyCallback,
  SearchSymbolsCallback,
  SymbolResolveExtension,
  DatafeedErrorCallback,
  IBasicDataFeed,
  IChartWidgetApi,
  Bar,
  Mark,
} from 'charting_library';

import { DexIntervalMapper } from './DexIntervalMapper';
import { DexHistoricalDataProvider } from './DexHistoricalDataProvider';
import type { CandleSeries } from '@/api/spec';
import { useCandlestickChartStore } from '../CandlestickChartStore.ts';
import useAppStore from '../../../store/rootStore.ts';
import { mapCandleSeriesToBars } from './mapCandleSeriesToBars.ts';
import { useTradeHistoryStore } from '@/feature/terminal_tables/trade_history/TradeHistoryStore.ts';
import { calculateTradeMarks } from './calculateTradeMarks.ts';

import { MAX_TRADE_MARKS } from '../chart_constants.ts';
import { isTradeMarksVisible } from '../chartOverlayConfig.ts';
import { tradeHistoryProvider } from '../TradeHistoryProvider.ts';

interface Subscription {
  callback: SubscribeBarsCallback;
  symbol: string;
  resolution: ResolutionString;
  onResetCacheNeededCallback: () => void;
}

export class DexCandlesDataFeed implements IBasicDataFeed {
  private static readonly SYMBOL_PRICESCALE = 100;
  private static readonly VOLUME_PRECISION = 2;
  private static readonly MINMOV = 1;
  private static readonly CACHE_TTL = 60_000;
  // The updates buffer only bridges `getBars` → `subscribeBars`; an older buffer is garbage.
  private static readonly UPDATES_CACHE_TTL = 60_000;
  // Caps the buffer: updates keep streaming (and landing here) while no chart is subscribed.
  private static readonly MAX_UPDATES_PER_KEY = 120;
  // Distinct `symbol_resolution` keys retained per cache.
  private static readonly MAX_CACHE_KEYS = 32;

  private static readonly DEFAULT_EXCHANGES = [
    {
      value: 'Perpl',
      name: 'Perpl',
      desc: 'Perpl Trade',
    },
  ];

  private static readonly DEFAULT_SYMBOL_TYPES = [
    {
      name: 'crypto',
      value: 'crypto',
    },
  ];

  private static readonly DEFAULT_FEATURE_FLAGS = {
    supports_marks: true,
    supports_timescale_marks: false,
    supports_time: false,
  };

  private static snapshotCache = new Map<string, { candleSeries: CandleSeries; touchedAt: number }>();
  private static updatesCache = new Map<string, { updates: CandleSeries[]; touchedAt: number }>();
  // Intentionally unbounded: tiny, and load-bearing for realtime bar ordering.
  private static lastBarTimes = new Map<string, number>();
  private static instance: DexCandlesDataFeed | null = null;

  private configurationData: DatafeedConfiguration;

  private static getCacheKey(symbol: string, resolution: ResolutionString): string {
    return `${symbol}_${resolution}`;
  }

  /** Drops expired keys, then least-recently-written past the cap (callers delete-then-set, so Map order = write recency). */
  private static pruneCache(cache: Map<string, { touchedAt: number }>, ttl: number, now: number): void {
    for (const [key, entry] of cache) {
      if (now - entry.touchedAt >= ttl) {
        cache.delete(key);
      }
    }
    for (const key of cache.keys()) {
      if (cache.size <= DexCandlesDataFeed.MAX_CACHE_KEYS) {
        break;
      }
      cache.delete(key);
    }
  }

  /** Buffered updates for a key, consuming the entry and discarding it if it aged out. */
  private static takeCachedUpdates(cacheKey: string): CandleSeries[] {
    const entry = DexCandlesDataFeed.updatesCache.get(cacheKey);
    if (!entry) {
      return [];
    }
    DexCandlesDataFeed.updatesCache.delete(cacheKey);
    return Date.now() - entry.touchedAt >= DexCandlesDataFeed.UPDATES_CACHE_TTL ? [] : entry.updates;
  }

  private getLastBarTime(symbol: string, resolution: ResolutionString): number {
    return DexCandlesDataFeed.lastBarTimes.get(DexCandlesDataFeed.getCacheKey(symbol, resolution)) || 0;
  }

  private updateLastBarTime(symbol: string, resolution: ResolutionString, time: number): void {
    const key = DexCandlesDataFeed.getCacheKey(symbol, resolution);
    const current = this.getLastBarTime(symbol, resolution);
    if (time >= current) {
      DexCandlesDataFeed.lastBarTimes.set(key, time);
    }
  }
  private intervalMapper = new DexIntervalMapper();
  private historicalDataProvider = new DexHistoricalDataProvider();

  private subscriptions = new Map<string, Subscription>();
  private candleUpdatesHandler: ((data: CandleSeries) => void) | null = null;
  // The ready chart this feed serves — registered by the widget hook, needed by reset() below.
  private activeChart: IChartWidgetApi | null = null;

  constructor() {
    this.configurationData = {
      supported_resolutions: this.intervalMapper.getSupportedResolutions(),
      exchanges: DexCandlesDataFeed.DEFAULT_EXCHANGES,
      symbols_types: DexCandlesDataFeed.DEFAULT_SYMBOL_TYPES,
      ...DexCandlesDataFeed.DEFAULT_FEATURE_FLAGS,
    };
  }

  static handleCandleSnapshot(marketId: string, data: CandleSeries): void {
    if (this.instance) {
      this.instance.handleCandleSnapshot(marketId, data);
    } else {
      this.cacheSnapshot(marketId, data);
    }
  }

  static handleCandleUpdate(marketId: string, data: CandleSeries): void {
    if (this.instance) {
      this.instance.handleCandleUpdate(marketId, data);
    } else {
      this.cacheUpdate(marketId, data);
    }
  }

  static onReconnect(): void {
    if (this.instance) {
      this.instance.reset();
    }
  }

  onReady(callback: OnReadyCallback): void {
    setTimeout(() => callback(this.configurationData), 0);
  }

  searchSymbols(userInput: string, exchange: string, symbolType: string, callback: SearchSymbolsCallback): void {}

  resolveSymbol(
    symbolName: string,
    onSymbolResolvedCallback: ResolveCallback,
    onResolveErrorCallback: DatafeedErrorCallback,
    extension?: SymbolResolveExtension
  ): void {
    const ticker = symbolName;
    const description = symbolName;
    const marketId = Number(symbolName);
    const market = useAppStore.getState().markets.get(marketId);
    const pricescale = market ? Math.pow(10, market.quoteDecimals) : DexCandlesDataFeed.SYMBOL_PRICESCALE;

    const symbolInfo: LibrarySymbolInfo = {
      ticker,
      name: symbolName,
      description,
      type: 'crypto',
      session: '24x7',
      timezone: 'Etc/UTC',
      exchange: 'Perpl',
      minmov: DexCandlesDataFeed.MINMOV,
      pricescale: pricescale,
      has_intraday: true,
      has_weekly_and_monthly: true,
      supported_resolutions: this.configurationData.supported_resolutions as ResolutionString[],
      volume_precision: DexCandlesDataFeed.VOLUME_PRECISION,
      data_status: 'streaming',
      base_name: [symbolName],
      listed_exchange: 'Perpl',
      format: 'price',
    };

    setTimeout(() => onSymbolResolvedCallback(symbolInfo), 0);
  }

  getBars(
    symbolInfo: LibrarySymbolInfo,
    resolution: ResolutionString,
    periodParams: PeriodParams,
    onHistoryCallback: HistoryCallback,
    onErrorCallback: DatafeedErrorCallback
  ): void {
    const { firstDataRequest } = periodParams;
    const symbol = symbolInfo.ticker || symbolInfo.name || '';

    // First load: use WS snapshot + backfill from REST API to cover the gap between them
    if (firstDataRequest) {
      const cachedSnapshot = this.getCachedSnapshot(symbol, resolution);
      const cacheKey = DexCandlesDataFeed.getCacheKey(symbol, resolution);

      if (cachedSnapshot) {
        const allCandleSeries: CandleSeries[] = [cachedSnapshot, ...DexCandlesDataFeed.takeCachedUpdates(cacheKey)];
        const mergedBars = this.mergeCandleSeriesToBars(allCandleSeries, symbol);

        if (mergedBars.length === 0) {
          onHistoryCallback([], { noData: true });
          return;
        }

        const lastBar = mergedBars[mergedBars.length - 1];
        this.updateLastBarTime(symbol, resolution, lastBar.time);

        // Fetch historical data leading up to the snapshot to fill the gap,
        // but only if the snapshot's earliest bar is after TV's requested `from`.
        // Otherwise the snapshot already covers the requested range and issuing
        // a backfill would produce a reversed `from > to` request.
        const firstBarTimeSec = Math.floor(mergedBars[0].time / 1000);
        if (firstBarTimeSec <= periodParams.from) {
          console.warn('Skipping backfill: snapshot covers requested range', {
            firstBarTimeSec,
            from: periodParams.from,
            symbol,
            resolution,
          });
          onHistoryCallback(mergedBars, { noData: false });
          return;
        }

        this.historicalDataProvider.getBars(
          symbolInfo,
          resolution,
          // countBack: 0 — TV's countBack is relative to the original `to` (now) and the
          // snapshot already covers that end; let the from→firstBar span drive the window
          // so the backfill fetches exactly the gap (and covers it fully when it's large).
          { ...periodParams, to: firstBarTimeSec, countBack: 0, firstDataRequest: false },
          (historicalBars) => {
            const combined = historicalBars.length > 0 ? [...historicalBars, ...mergedBars] : mergedBars;
            onHistoryCallback(combined, { noData: false });
          },
          () => {
            // On error, still return snapshot bars
            onHistoryCallback(mergedBars, { noData: false });
          }
        );
        return;
      }
    }

    // Pagination / no snapshot: fetch purely from REST API
    this.historicalDataProvider.getBars(
      symbolInfo,
      resolution,
      periodParams,
      (bars, meta) => {
        if (bars.length > 0) {
          const lastBar = bars[bars.length - 1];
          this.updateLastBarTime(symbol, resolution, lastBar.time);
        }
        onHistoryCallback(bars, meta);
      },
      onErrorCallback
    );
  }

  async getMarks(
    symbolInfo: LibrarySymbolInfo,
    from: number,
    to: number,
    onDataCallback: (marks: Mark[]) => void,
    resolution: ResolutionString
  ): Promise<void> {
    if (!isTradeMarksVisible(useCandlestickChartStore.getState().overlayVisibility)) {
      onDataCallback([]);
      return;
    }

    try {
      await tradeHistoryProvider.loadUntil(from);
    } catch (e) {
      // Backfill failures must not swallow the request: TV only extends its loaded-marks range
      // from inside `onDataCallback`, so an unanswered request leaves the range permanently
      // unmarked and re-issued on every later pan/zoom. Draw what's already loaded instead.
      // Reachable whenever the auth nonce has expired — the fetch throws before any page lands.
      console.warn('Trade marks: history backfill failed, using already loaded trades', e);
    }

    const allTrades = useTradeHistoryStore.getState().items;
    const symbol = symbolInfo.ticker || symbolInfo.name || '';

    const filteredTrades = allTrades.filter((trade) => {
      const tradeTime = Math.floor(trade.dateTimestamp / 1000);
      return trade.marketId.toString() === symbol && tradeTime >= from && tradeTime <= to && trade.size > 0 && trade.price > 0;
    });

    const exchangeInterval = this.intervalMapper.getExchangeInterval(resolution);
    const candleDurationSeconds = exchangeInterval ? parseInt(exchangeInterval, 10) : 0;

    const marks = calculateTradeMarks(filteredTrades, candleDurationSeconds, MAX_TRADE_MARKS);

    onDataCallback(marks);
  }

  private mergeCandleSeriesToBars(candleSeriesList: CandleSeries[], symbol: string): Bar[] {
    const barsMap = new Map<number, Bar>();

    candleSeriesList.forEach((candleSeries) => {
      const bars = mapCandleSeriesToBars(candleSeries, symbol);
      bars.forEach((bar) => {
        barsMap.set(bar.time, bar);
      });
    });

    return Array.from(barsMap.values()).sort((a, b) => a.time - b.time);
  }

  /** Chart handle for reset(); registered on chart-ready, cleared at teardown before widget removal. */
  setActiveChart(chart: IChartWidgetApi | null): void {
    this.activeChart = chart;
  }

  reset(): void {
    // Invalidate-then-refetch, in that order (the documented resetData() contract). With
    // request_only_visible_range_on_reset enabled, onResetCacheNeededCallback() alone makes
    // the bundle unsubscribe realtime and purge its bar cache WITHOUT issuing a re-request —
    // only resetData() triggers the visible-range refetch whose completion also re-subscribes
    // realtime. Skipping it leaves the chart permanently frozen after a WS reconnect.
    this.subscriptions.forEach((subscription) => {
      subscription.onResetCacheNeededCallback();
    });
    try {
      this.activeChart?.resetData();
    } catch {
      // Chart mid-teardown; the next widget registers a fresh handle and loads fresh data anyway.
    }
  }

  /**
   * Sweeps subscriptions TradingView failed to remove — a stale entry's callback retains the dead
   * chart's whole iframe and keeps receiving ticks. Called from the widget effect cleanup.
   */
  dispose(): void {
    this.subscriptions.clear();
    this.cleanupCandleUpdatesHandler();
    this.activeChart = null;
  }

  subscribeBars(
    symbolInfo: LibrarySymbolInfo,
    resolution: ResolutionString,
    onRealtimeCallback: SubscribeBarsCallback,
    subscriberUID: string,
    onResetCacheNeededCallback: () => void
  ): void {
    const symbol = symbolInfo.ticker || symbolInfo.name || '';
    const exchangeInterval = this.intervalMapper.getExchangeInterval(resolution);

    if (!exchangeInterval) {
      console.error('Unsupported resolution:', resolution);
      return;
    }

    this.subscriptions.set(subscriberUID, {
      callback: onRealtimeCallback,
      symbol,
      resolution,
      onResetCacheNeededCallback,
    });

    this.setupCandleUpdatesHandler();

    // Process any updates that arrived between getBars and subscribeBars
    const cacheKey = DexCandlesDataFeed.getCacheKey(symbol, resolution);
    const cachedUpdates = DexCandlesDataFeed.takeCachedUpdates(cacheKey);

    if (cachedUpdates.length > 0) {
      const updateBars = this.mergeCandleSeriesToBars(cachedUpdates, symbol);

      updateBars.forEach((bar) => {
        const lastTime = this.getLastBarTime(symbol, resolution);
        if (bar.time >= lastTime) {
          this.updateLastBarTime(symbol, resolution, bar.time);
          onRealtimeCallback(bar);
        }
      });
    }
  }

  unsubscribeBars(subscriberUID: string): void {
    this.subscriptions.delete(subscriberUID);

    if (this.subscriptions.size === 0) {
      this.cleanupCandleUpdatesHandler();
    }
  }

  static cacheSnapshotBars(symbol: string, resolution: ResolutionString, candleSeries: CandleSeries): void {
    const key = DexCandlesDataFeed.getCacheKey(symbol, resolution);
    const now = Date.now();
    DexCandlesDataFeed.snapshotCache.delete(key);
    DexCandlesDataFeed.snapshotCache.set(key, { candleSeries, touchedAt: now });
    DexCandlesDataFeed.pruneCache(DexCandlesDataFeed.snapshotCache, DexCandlesDataFeed.CACHE_TTL, now);
  }

  getCachedSnapshot(symbol: string, resolution: ResolutionString): CandleSeries | null {
    const key = DexCandlesDataFeed.getCacheKey(symbol, resolution);
    const cached = DexCandlesDataFeed.snapshotCache.get(key);

    if (cached && Date.now() - cached.touchedAt < DexCandlesDataFeed.CACHE_TTL) {
      return cached.candleSeries;
    }

    if (cached) {
      DexCandlesDataFeed.snapshotCache.delete(key);
    }

    return null;
  }

  handleCandleUpdate(marketId: string, data: CandleSeries): void {
    if (!data.d || data.d.length === 0) return;

    const resolution = useCandlestickChartStore.getState().resolution;
    if (!resolution) return;

    const symbol = marketId?.toString() || '';
    const bars = mapCandleSeriesToBars(data, symbol);
    this.forwardBarsToSubscribers(bars, symbol, resolution);
  }

  handleCandleSnapshot(marketId: string, data: CandleSeries): void {
    if (!data.d || data.d.length === 0) return;

    const resolution = useCandlestickChartStore.getState().resolution;
    if (!resolution) return;

    const symbol = marketId?.toString() || '';
    DexCandlesDataFeed.cacheSnapshotBars(symbol, resolution, data);

    // Forward to subscribers to fill gaps between snapshot and last known bar
    const bars = mapCandleSeriesToBars(data, symbol);
    this.forwardBarsToSubscribers(bars, symbol, resolution);
  }

  /** Send bars to matching subscribers, skipping bars older than the last dispatched bar */
  private forwardBarsToSubscribers(bars: Bar[], symbol: string, resolution: ResolutionString): void {
    bars.forEach((bar) => {
      this.subscriptions.forEach((subscription) => {
        if (subscription.symbol === symbol && subscription.resolution === resolution) {
          const lastTime = this.getLastBarTime(symbol, resolution);
          if (bar.time >= lastTime) {
            this.updateLastBarTime(symbol, resolution, bar.time);
            subscription.callback(bar);
          }
        }
      });
    });
  }

  static cacheSnapshot(marketId: string, candleSeries: CandleSeries): void {
    const resolution = useCandlestickChartStore.getState().resolution;
    if (resolution) {
      DexCandlesDataFeed.cacheSnapshotBars(marketId, resolution, candleSeries);
    } else {
      console.warn('DEX candles data feed not available and no resolution set, candle snapshot ignored');
    }
  }

  static cacheUpdate(marketId: string, candleSeries: CandleSeries): void {
    const resolution = useCandlestickChartStore.getState().resolution;
    if (!resolution) {
      return;
    }

    const cacheKey = DexCandlesDataFeed.getCacheKey(marketId, resolution);
    const now = Date.now();
    const existing = this.updatesCache.get(cacheKey);
    const existingUpdates = existing && now - existing.touchedAt < DexCandlesDataFeed.UPDATES_CACHE_TTL ? existing.updates : [];

    const newCandleTimestamps = new Set(candleSeries.d?.map((candle) => candle.t));

    const updatedExistingUpdates = existingUpdates
      .map((existingUpdate) => ({
        ...existingUpdate,
        d: existingUpdate.d?.filter((candle) => !newCandleTimestamps.has(candle.t)),
      }))
      .filter((existingUpdate) => existingUpdate.d && existingUpdate.d.length > 0);

    updatedExistingUpdates.push(candleSeries);

    // Post-dedup this grows one entry per candle period; keep the newest.
    if (updatedExistingUpdates.length > DexCandlesDataFeed.MAX_UPDATES_PER_KEY) {
      updatedExistingUpdates.splice(0, updatedExistingUpdates.length - DexCandlesDataFeed.MAX_UPDATES_PER_KEY);
    }

    this.updatesCache.delete(cacheKey);
    this.updatesCache.set(cacheKey, { updates: updatedExistingUpdates, touchedAt: now });
    DexCandlesDataFeed.pruneCache(this.updatesCache, DexCandlesDataFeed.UPDATES_CACHE_TTL, now);
  }

  private setupCandleUpdatesHandler(): void {
    if (this.candleUpdatesHandler) return;

    this.candleUpdatesHandler = (data: CandleSeries) => {
      this.handleCandleUpdate(useAppStore.getState().selectedMarketId?.toString() || '', data);
    };

    DexCandlesDataFeed.instance = this;
  }

  private cleanupCandleUpdatesHandler(): void {
    this.candleUpdatesHandler = null;
    // A disposed instance must not null a live successor's pointer.
    if (DexCandlesDataFeed.instance === this) {
      DexCandlesDataFeed.instance = null;
    }
  }
}
